// Skill level types for assignment requirements
export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

// Skill requirement for assignment matching
export interface AssignmentSkillRequirement {
    skillName: string;
    trade: AssignmentTradeType;
    minimumLevel: SkillLevel;
    isRequired: boolean; // If true, employee must have this skill
}

// Assignment location with coordinates for distance calculations
export interface AssignmentLocation {
    address?: string;
    coordinates?: {
        latitude: number;
        longitude: number;
    };
}

export interface AssignmentModel extends CreateAssignmentModel {
    id: string;
    createdAt: string;
    updatedAt: string;

    // Financial Tracking (calculated fields)
    actualRevenue?: number;
    actualCosts?: number;
    profit?: number;
    profitMargin?: number;

    // Time Tracking
    actualHours?: number;

    // Invoice linkage
    invoiceIds?: string[];
    dineroInvoiceGuids?: string[];
    economicInvoiceNumbers?: number[];
}

export interface CreateAssignmentModel {
    // Project linkage
    projectId: string;
    projectTitle: string;

    // Assignment details
    title: string;
    description: string;
    notes?: string;

    // Assignee information (optional — tasks can be created unassigned for later planning)
    assigneeId?: string;
    assigneeName?: string;
    assigneeType?: AssignmentAssigneeType;

    // Trade/category
    trade: AssignmentTradeType;
    customTrade?: string;

    // Timeline
    startDate: string;
    endDate: string;
    startTime?: string;  // HH:MM format, e.g., "08:00" - optional for hour-level scheduling
    endTime?: string;    // HH:MM format, e.g., "16:30" - optional for hour-level scheduling

    // Status
    status: AssignmentStatusModel;

    // Financial
    price: number;
    estimatedHours?: number;
    hourlyRate?: number;
    materialCosts?: number;
    laborCosts?: number;
    includeVAT?: boolean;
    hidePrice?: boolean;  // If true, price info is hidden from the assignee

    // Company & creator
    companyId: string;
    createdById: string;
    createdByName: string;

    // Aftaleseddel linkage (change order)
    aftaleseddelId?: string;

    // Customer reference (for invoicing)
    clientId: string;
    clientName: string;
    dineroContactGuid?: string;
    economicCustomerNumber?: number;

    // Smart matching fields
    location?: AssignmentLocation;

    // Ejendom link. Sits alongside `location` above, which keeps working. Usually inherited
    // from the parent project at create time — one project normally means one building.
    propertyId?: string;
    propertyUnitId?: string;
    requiredSkills?: AssignmentSkillRequirement[];
    maxEmployeeCostPerHour?: number; // Budget constraint for employee selection
}

export type AssignmentStatusModel = 'pending' | 'in-progress' | 'completed' | 'cancelled' | 'invoiced';

export type AssignmentAssigneeType = 'internal' | 'external';

export type AssignmentTradeType =
    | 'carpenter'
    | 'electrician'
    | 'plumber'
    | 'bricklayer'
    | 'painter'
    | 'roofer'
    | 'hvac'
    | 'landscaper'
    | 'general_contractor'
    | 'other';

export const TRADE_LABELS: Record<AssignmentTradeType, string> = {
    carpenter: 'Tømrer',
    electrician: 'Elektriker',
    plumber: 'VVS',
    bricklayer: 'Murer',
    painter: 'Maler',
    roofer: 'Tækker',
    hvac: 'Ventilation',
    landscaper: 'Anlægsgartner',
    general_contractor: 'Hovedentreprenør',
    other: 'Andet',
};

export const STATUS_LABELS: Record<AssignmentStatusModel, string> = {
    pending: 'Afventer',
    'in-progress': 'I gang',
    completed: 'Afsluttet',
    cancelled: 'Annulleret',
    invoiced: 'Faktureret',
};
